import { action } from "typesafe-actions";

import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IUserDocument } from "./userDocument.types";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { uploadFile } from "../../components/FileUpload/utils";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";

export const FETCH_USER_DOCUMENTS_LIST_PROGRESS =
  "FETCH_USER_DOCUMENTS_LIST_PROGRESS";
export const FETCH_USER_DOCUMENTS_LIST_SUCCESS =
  "FETCH_USER_DOCUMENTS_LIST_SUCCESS";
export const FETCH_USER_DOCUMENTS_LIST_FAILED =
  "FETCH_USER_DOCUMENTS_LIST_FAILED";

export const fetchUserDocumentsListProgress = () =>
  action(FETCH_USER_DOCUMENTS_LIST_PROGRESS);
export const fetchUserDocumentsListSuccess = (
  data: IUserDocument[],
  totalRecords: number,
) => action(FETCH_USER_DOCUMENTS_LIST_SUCCESS, { data, totalRecords });
export const fetchUserDocumentsListFailed = () =>
  action(FETCH_USER_DOCUMENTS_LIST_FAILED);

export const fetchUserDocumentsListAsync =
  (
    user_id: string,
    page: number,
    rowsPerPage: number,
    limit: number,
    date: IDataTableV2DateState["dates"],
    company_outlet_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchUserDocumentsListProgress());
      let url = `/user/get-user-attachment?user_uuid=${user_id}&pageNo=${page}&pageLimit=${limit}&itemPerPage=${rowsPerPage}`;
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      if (date?.fromDate && date?.toDate) {
        url += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }
      const res = await api.get(url);
      const apiData = res.data.data;

      // Map API response to IUserDocument format
      const data: IUserDocument[] = apiData.map((item: any) => ({
        ...item,
        // Create legacy fields for backward compatibility
        user_attachment_uuid: item.user_attachments_uuid,
        document_type: item.description || "Document",
        file_url:
          item.attachments && item.attachments.length > 0
            ? item.attachments[0]
            : "",
        company_outlet_name: item.company_outlet_uuid, // This might need to be fetched separately
      }));

      dispatch(fetchUserDocumentsListSuccess(data, res.data.totalRecords));
    } catch (err: any) {
      dispatch(fetchUserDocumentsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const addUserDocumentsAsync =
  (
    data: IUserDocument,
    file: any | null,
    onCallback: (isSuccess: boolean, path?: string) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      const path = await uploadFile({
        file: file,
        module_name: "USER",
        defaultReturn: data.attachments[0] || "",
        asPayload: {},
        onWarning: (message) => {
          dispatch(
            showMessage({
              type: "warning",
              message: message,
              displayAs: "snackbar",
            }),
          );
        },
      });

      const payload: any = {
        user_attachments_uuid: data.user_attachments_uuid || undefined,
        user_uuid: data.user_uuid,
        description: data.description,
        company_outlet_uuid: data.company_outlet_uuid,
        status: data.status,
        created_by_uuid: data.created_by_uuid,
        created_by_name: data.created_by_name,
        modified_by_uuid: data.modified_by_uuid,
        modified_by_name: data.modified_by_name,
        attachments: [path],
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      await api.post("/user/upsert-user-attachment", payload);

      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: "Document saved successfully!",
          displayAs: "snackbar",
        }),
      );
      onCallback(true, path);
    } catch (err: any) {
      onCallback(false, "");
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchUserDocumentsAsync =
  (
    user_uuid: string,
    company_outlet_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchUserDocumentsListProgress());
      dispatch(saveLoaderProgress());
      let url = `/user-documents/get-user-documents?user_uuid=${user_uuid}`;
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }
      const res = await api.get(url);
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchUserDocumentsListSuccess([data[0]], res.data.totalRecords),
        );
      } else {
        dispatch(fetchUserDocumentsListFailed());
      }
    } catch (err: any) {
      dispatch(fetchUserDocumentsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
